import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { ErrorBox, StatusChip } from '../components/Layout';
import { QrCode } from '../components/QrCode';
import { laoDateTime, money } from '../lib/money';
import { PROOF_LABEL, STATUS_LABEL, type Payment, type WebhookDelivery } from '../types';

/** ຂະນະລໍຖ້າເງິນ ໂຫຼດໃໝ່ທຸກ 4 ວິນາທີ */
const POLL_MS = 4_000;

export function PaymentDetailPage() {
  const { paymentId = '' } = useParams();
  const [payment, setPayment] = useState<Payment | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.payment(paymentId);
      setPayment(data.payment);
      setDeliveries(data.deliveries);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ໂຫຼດຂໍ້ມູນບໍ່ໄດ້');
    }
  }, [paymentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = payment?.status === 'pending';
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [pending, load]);

  async function act(action: 'confirm' | 'cancel') {
    const question =
      action === 'confirm'
        ? 'ຢືນຢັນວ່າເງິນເຂົ້າບັນຊີແລ້ວແທ້? (ກວດໃນ BCEL One ກ່ອນ)'
        : 'ຍົກເລີກ QR ນີ້?';
    if (!window.confirm(question)) return;

    setBusy(true);
    try {
      const { payment: updated } =
        action === 'confirm'
          ? await api.confirm(paymentId, note)
          : await api.cancel(paymentId, note);
      setPayment(updated);
      setNote('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ບໍ່ສຳເລັດ');
    } finally {
      setBusy(false);
    }
  }

  async function retry(id: number) {
    try {
      await api.retryWebhook(id);
      await load();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ສົ່ງໃໝ່ບໍ່ສຳເລັດ');
    }
  }

  if (!payment) {
    return (
      <>
        <p>
          <Link to="/payments">← ປະຫວັດ</Link>
        </p>
        <ErrorBox error={error} />
        {!error && <p className="muted">ກຳລັງໂຫຼດ…</p>}
      </>
    );
  }

  const offset = Number(payment.amount) - Number(payment.requestedAmount);

  return (
    <>
      <div className="card detail-hero">
        <div className="detail-hero-title">
          <Link to="/payments" className="detail-back">
            ← ກັບຄືນ
          </Link>
          <h1>{money(payment.amount)}</h1>
        </div>
        <StatusChip status={payment.status} label={STATUS_LABEL[payment.status] ?? payment.status} />
      </div>
      <ErrorBox error={error} />

      <div className="detail-grid">
        {pending && (
          <section className={`card center detail-qr detail-accent detail-accent-${payment.status}`}>
            <QrCode value={payment.qrCode} size={200} />
            <p className="qr-amount">{money(payment.amount)}</p>
            <p className="hint">
              ສະແກນແລ້ວຍອດຂຶ້ນເອງ (BCEL One ແລະ ທະນາຄານອື່ນ)
            </p>
            <p className="muted">ໝົດອາຍຸ {laoDateTime(payment.expiresAt)}</p>
          </section>
        )}

        <section className={`card detail-accent detail-accent-${payment.status}`}>
          <dl className="summary">
            <dt>ລະຫັດ</dt>
            <dd>{payment.paymentId}</dd>
            <dt>ລາຍລະອຽດ</dt>
            <dd>{payment.description}</dd>
            {payment.orderNo && (
              <>
                <dt>ເລກອ້າງອີງ</dt>
                <dd>{payment.orderNo}</dd>
              </>
            )}
            <dt>ຍອດທີ່ຂໍ</dt>
            <dd>
              {money(payment.requestedAmount)}
              {offset > 0 && <span className="muted"> (+{offset} ກີບ ເພື່ອແຍກລາຍການ)</span>}
            </dd>
            <dt>ສ້າງເມື່ອ</dt>
            <dd>{laoDateTime(payment.createdAt)}</dd>
            {payment.paidAt && (
              <>
                <dt>ຈ່າຍເມື່ອ</dt>
                <dd>{laoDateTime(payment.paidAt)}</dd>
              </>
            )}
            {payment.proof && (
              <>
                <dt>ຫຼັກຖານ</dt>
                <dd>
                  {PROOF_LABEL[payment.proof.source] ?? payment.proof.source}
                  {payment.proof.reference && ` · ${payment.proof.reference}`}
                </dd>
              </>
            )}
            {payment.note && (
              <>
                <dt>ໝາຍເຫດ</dt>
                <dd>{payment.note}</dd>
              </>
            )}
            {payment.metadata && (
              <>
                <dt>ຂໍ້ມູນແນບ</dt>
                <dd>
                  <code>{JSON.stringify(payment.metadata)}</code>
                </dd>
              </>
            )}
          </dl>

          {pending && (
            <div className="actions">
              <label>
                ໝາຍເຫດ (ບໍ່ບັງຄັບ)
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={255}
                  placeholder="ເຊັ່ນ ກວດໃນ BCEL One ແລ້ວ"
                />
              </label>
              <button type="button" className="pay" disabled={busy} onClick={() => act('confirm')}>
                ໄດ້ຮັບເງິນແລ້ວ
              </button>
              <button
                type="button"
                className="btn-outline-bad"
                disabled={busy}
                onClick={() => act('cancel')}
              >
                ຍົກເລີກ QR
              </button>
            </div>
          )}
        </section>
      </div>

      <section>
        <h2 className="section-title">ປະຫວັດ Webhook</h2>
        {deliveries.length === 0 ? (
          <p className="muted">ຍັງບໍ່ມີການສົ່ງ</p>
        ) : (
          <ul className="admin-orders">
            {deliveries.map((d) => (
              <li className="card admin-order" key={d.id}>
                <div className="admin-order-head">
                  <strong>{d.event}</strong>
                  <StatusChip status={d.status} label={STATUS_LABEL[d.status] ?? d.status} />
                </div>
                <p className="muted">
                  {laoDateTime(d.createdAt)} · ພະຍາຍາມ {d.attempts} ຄັ້ງ · {d.url}
                </p>
                {d.lastError && <p className="alert">{d.lastError}</p>}
                {d.status === 'failed' && (
                  <button type="button" className="link-button" onClick={() => retry(d.id)}>
                    ສົ່ງໃໝ່
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
